import { CssBaseline, createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { generateTheme, ThemeMeta } from '@tix-ticketing/ui-theme';
import Head from 'next/head';
import { useMemo, type FC, type ReactNode } from 'react';
import { useColorScheme } from 'lib/hooks/use-color-scheme';
import { useThemeConfig, type ThemeConfig } from 'lib/theme-config';
type Props = {
  children: ReactNode;
};
const neutralThemeConfig: ThemeConfig = {
  theme: {}
};

/**
 * The tenant theme is unvalidated JSON from `/appsettings`, so a bad value
 * (e.g. a colour MUI cannot parse) makes `createTheme` throw. Fall back to the
 * neutral defaults rather than taking the whole app down.
 */
export function buildTheme(themeConfig: ThemeConfig, colorScheme: 'light' | 'dark') {
  try {
    return createTheme(generateTheme(themeConfig, colorScheme));
  } catch (err) {
    console.error('[theme] tenant theme is invalid; using neutral defaults:', err);
    return createTheme(generateTheme(neutralThemeConfig, 'light'));
  }
}
export const ThemeProvider: FC<Props> = props => {
  const themeConfig = useThemeConfig();
  const colorScheme = useColorScheme();
  const theme = useMemo(() => buildTheme(themeConfig, colorScheme), [themeConfig, colorScheme]);
  return <MuiThemeProvider theme={theme}>
      <Head>
        <ThemeMeta appConfig={themeConfig} colorScheme={colorScheme} />
      </Head>
      <CssBaseline />
      {props.children}
    </MuiThemeProvider>;
};